/**
 * Auth state — Zustand store with localStorage persistence.
 * Holds the access + refresh JWTs and a minimal user profile snapshot.
 * The actual user document is fetched fresh via React Query when needed.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Roles as of Phase 10b. 'ops' and 'rider' are the pre-rename values —
 * still present in the database until migrate-user-roles.mjs runs, and the
 * backend's ROLE_ALIASES maps them to ops_manager / driver in the meantime.
 * Keep both here or a legacy account fails to type-check on sign-in.
 */
export type UserRole =
  | 'customer' | 'super_admin' | 'admin' | 'ops_manager'
  | 'supervisor' | 'driver' | 'cs_agent'
  | 'ops' | 'rider';

export interface AuthUser {
  id: string;
  phone: string;
  name?: string;
  role: UserRole;
  preferred_locale?: 'en' | 'ar';
  /**
   * Expanded permission list from the backend's matrix, returned by
   * /auth/verify-otp, /auth/staff/login and /auth/me. The admin sidebar
   * gates off this rather than hardcoding role names, so a permission
   * change on the server needs no frontend deploy.
   */
  permissions?: string[];
}

/** Does the signed-in user hold this permission? Wildcards expand downward. */
export function hasPermission(user: AuthUser | null, permission: string): boolean {
  if (!user) return false;
  const held = user.permissions;
  // No permissions array means a token minted before Phase 10b. Fall back to
  // role so an existing session isn't locked out mid-flight.
  if (!held || held.length === 0) {
    return user.role === 'admin' || user.role === 'super_admin';
  }
  if (held.includes('*')) return true;
  if (held.includes(permission)) return true;
  return held.some((p) => p.endsWith('.*') && permission.startsWith(p.slice(0, -1)));
}

/** True for any non-customer role — used to route staff away from /account. */
export function isStaff(user: AuthUser | null): boolean {
  return !!user && user.role !== 'customer';
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  setTokens: (access: string, refresh: string) => void;
  setUser: (user: AuthUser) => void;
  setSession: (access: string, refresh: string, user: AuthUser) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setTokens: (access, refresh) => set({ accessToken: access, refreshToken: refresh }),
      setUser: (user) => set({ user }),
      setSession: (access, refresh, user) => set({ accessToken: access, refreshToken: refresh, user }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null }),
      isAuthenticated: () => !!get().accessToken && !!get().user,
    }),
    {
      name: 'tlc-auth',
      storage: createJSONStorage(() => localStorage),
      // Don't persist computed selectors — only the data
      partialize: (s) => ({ accessToken: s.accessToken, refreshToken: s.refreshToken, user: s.user }),
    },
  ),
);
